import clsx from "clsx";
import { CloudOff, RefreshCw, TriangleAlert } from "lucide-react";
import { Link } from "wouter";
import type { DatasetRow, SyncResponse } from "../../shared/sync";
import { phoneView } from "../lib/api";
import { count, timeAgo } from "../lib/format";
import { type CompanySummary, useSyncStatus } from "../lib/queries";
import type { ConnectionState } from "../lib/realtime";
import { useWithWindows } from "./ToolWindows";
import { Popover } from "./ui";

/*
 * The sync's state in the header, for everyone: "Up to date", "Syncing Jobs", or a warning when
 * something needs attention; "Offline" while this page has lost its live connection to the server.
 * Managers click through to Settings › ServiceM8 (the details and controls). Everyone else (and
 * phones, which don't show Settings) gets a short summary in a dropdown.
 */

/** The sync's state in a few words, most important first. */
function summary(data: SyncResponse): { text: string; warning: boolean; spinning: boolean } {
    const running = data.datasets.find((d) => d.status === "running");
    const problem = data.datasets.find((d) => d.status === "error");
    // Only data actually syncing or waiting its turn: not data that's off, or that ServiceM8 hasn't
    // switched on (Check again only asks ServiceM8 again).
    const firstSync = data.datasets.find(
        (d) => d.full.state !== "done" && d.unavailable === null && ["running", "queued", "waiting"].includes(d.status),
    );
    const spinning = Boolean(running);
    if (!data.connected) return { text: "Not syncing", warning: true, spinning };
    if (problem) return { text: `Problem syncing ${problem.name}`, warning: true, spinning };
    if (data.paused) return { text: "Sync paused", warning: false, spinning };
    if (running?.running === "full" && running.kind === "windowed") {
        return { text: `Syncing ${running.name} (${count(running.full.fetched)})`, warning: false, spinning };
    }
    if (running) return { text: `Syncing ${running.name}`, warning: false, spinning };
    if (firstSync) return { text: `${firstSync.name}: first sync in progress`, warning: false, spinning };
    return { text: "Up to date", warning: false, spinning };
}

export function SyncStatus({ company, realtime }: Readonly<{ company: CompanySummary; realtime: ConnectionState }>) {
    const { data } = useSyncStatus(company.id);
    const withWindows = useWithWindows();
    if (realtime === "closed") {
        return (
            <span className="flex items-center gap-1.5 text-sm text-warning" title="Reconnecting to the server…">
                <CloudOff className="size-4" /> Offline
            </span>
        );
    }
    if (!data) return null;
    const { text, warning, spinning } = summary(data);
    const className = clsx("flex cursor-pointer items-center gap-1.5 text-sm whitespace-nowrap no-underline hover:underline", warning ? "text-warning" : "text-muted");
    const shown = (
        <>
            <RefreshCw className={clsx("size-3.5", spinning && "animate-spin [animation-duration:2s]")} />
            {text}
        </>
    );
    if (company.canManage && !phoneView) {
        return (
            <Link href={withWindows("/settings/servicem8")} className={className} title="See what's synced, in Settings">
                {shown}
            </Link>
        );
    }
    return (
        <Popover label="Sync status" buttonClassName={className} button={shown}>
            {() => <Details data={data} headline={text} canManage={company.canManage} />}
        </Popover>
    );
}

/** What's going on, in a few lines: the state, when it last updated, and anything wrong (and who can fix it). */
function Details({ data, headline, canManage }: Readonly<{ data: SyncResponse; headline: string; canManage: boolean }>) {
    const problems: DatasetRow[] = data.connected ? data.datasets.filter((d) => d.status === "error" || d.status === "no-permission") : [];
    const last = Math.max(0, ...data.datasets.map((d) => d.lastSyncAt ?? 0));
    const fixer = canManage ? "You can sort it out in Settings, on a computer." : "Let a manager know: they can sort it out in Settings.";
    return (
        <div className="space-y-2 px-3 py-2">
            <div>
                <div className="font-semibold">{headline}</div>
                <div className="text-xs text-muted">
                    A copy of your ServiceM8 data, kept up to date automatically.{last ? ` Last updated ${timeAgo(last)}.` : ""}
                </div>
            </div>
            {data.connected ? null : <Problem text="ServiceM8 isn't connected, so nothing is being updated." />}
            {problems.map((d) => (
                <Problem key={d.id} text={d.status === "no-permission" ? `${d.name}: ServiceM8 hasn't given permission to read these.` : `${d.name}: couldn't update.`} />
            ))}
            {!data.connected || problems.length ? <div className="text-xs text-muted">{fixer}</div> : null}
        </div>
    );
}

function Problem({ text }: Readonly<{ text: string }>) {
    return (
        <div className="flex items-start gap-2 text-sm">
            <TriangleAlert className="mt-0.5 size-4 shrink-0 text-warning" />
            <span>{text}</span>
        </div>
    );
}

/**
 * The same state as a line of text, not a link (it sits inside the sidebar's account button, which
 * opens the account menu: Settings is there for the details).
 */
export function SyncLine({ company, realtime }: Readonly<{ company: CompanySummary; realtime: ConnectionState }>) {
    const { data } = useSyncStatus(company.id);
    if (realtime === "closed") {
        return (
            <span className="flex items-center gap-1.5 text-xs text-warning" title="Reconnecting to the server…">
                <CloudOff className="size-3.5 shrink-0" /> Offline
            </span>
        );
    }
    if (!data) return null;
    const { text, warning, spinning } = summary(data);
    return (
        <span className={clsx("flex min-w-0 items-center gap-1.5 text-xs", warning ? "text-warning" : "text-muted")} title={text}>
            <RefreshCw className={clsx("size-3.5 shrink-0", spinning && "animate-spin [animation-duration:2s]")} />
            <span className="truncate">{text}</span>
        </span>
    );
}
